type BrowserAudioContext = typeof AudioContext

declare global {
  interface Window {
    webkitAudioContext?: BrowserAudioContext
  }
}

export const TARGET_SAMPLE_RATE = 16000

const writeString = (view: DataView, offset: number, value: string) => {
  for (let index = 0; index < value.length; index += 1) {
    view.setUint8(offset + index, value.charCodeAt(index))
  }
}

export const downsampleAudioSamples = (samples: Float32Array, sourceRate: number) => {
  if (sourceRate === TARGET_SAMPLE_RATE) return samples

  const ratio = sourceRate / TARGET_SAMPLE_RATE
  const length = Math.round(samples.length / ratio)
  const result = new Float32Array(length)

  for (let index = 0; index < length; index += 1) {
    const sourceIndex = index * ratio
    const before = Math.floor(sourceIndex)
    const after = Math.min(before + 1, samples.length - 1)
    const weight = sourceIndex - before
    result[index] = samples[before] * (1 - weight) + samples[after] * weight
  }

  return result
}

export const encodeWav = (samples: Float32Array, sampleRate: number) => {
  const bytesPerSample = 2
  const buffer = new ArrayBuffer(44 + samples.length * bytesPerSample)
  const view = new DataView(buffer)

  writeString(view, 0, 'RIFF')
  view.setUint32(4, 36 + samples.length * bytesPerSample, true)
  writeString(view, 8, 'WAVE')
  writeString(view, 12, 'fmt ')
  view.setUint32(16, 16, true)
  view.setUint16(20, 1, true)
  view.setUint16(22, 1, true)
  view.setUint32(24, sampleRate, true)
  view.setUint32(28, sampleRate * bytesPerSample, true)
  view.setUint16(32, bytesPerSample, true)
  view.setUint16(34, 8 * bytesPerSample, true)
  writeString(view, 36, 'data')
  view.setUint32(40, samples.length * bytesPerSample, true)

  let offset = 44
  for (const sample of samples) {
    const clamped = Math.max(-1, Math.min(1, sample))
    view.setInt16(offset, clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff, true)
    offset += bytesPerSample
  }

  return new Blob([view], { type: 'audio/wav' })
}

export const recordWavChunk = async (stream: MediaStream, durationMs: number) => {
  const audioTracks = stream.getAudioTracks().filter((track) => track.readyState === 'live')
  if (!audioTracks.length) {
    throw new Error('No hay audio local activo para grabar.')
  }

  const AudioContextClass = window.AudioContext || window.webkitAudioContext
  if (!AudioContextClass) {
    throw new Error('El navegador no soporta AudioContext.')
  }

  const audioContext = new AudioContextClass()
  const source = audioContext.createMediaStreamSource(new MediaStream(audioTracks))
  const processor = audioContext.createScriptProcessor(4096, 1, 1)
  const chunks: Float32Array[] = []

  processor.onaudioprocess = (event) => {
    chunks.push(new Float32Array(event.inputBuffer.getChannelData(0)))
  }

  source.connect(processor)
  processor.connect(audioContext.destination)

  try {
    await new Promise((resolve) => window.setTimeout(resolve, durationMs))
  } finally {
    processor.disconnect()
    source.disconnect()
    await audioContext.close()
  }

  const sampleCount = chunks.reduce((total, chunk) => total + chunk.length, 0)
  const merged = new Float32Array(sampleCount)
  let offset = 0
  for (const chunk of chunks) {
    merged.set(chunk, offset)
    offset += chunk.length
  }

  return encodeWav(downsampleAudioSamples(merged, audioContext.sampleRate), TARGET_SAMPLE_RATE)
}
